import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Janela flutuante do sistema (Document Picture-in-Picture).
 *
 * Diferente do PiP comum, que so leva UM video e nao aceita controle nenhum,
 * este leva HTML de verdade: o palco inteiro vai pra janelinha, com mosaico,
 * etiquetas e botoes. E ela fica por cima de qualquer aplicativo — que e o
 * ponto, ja que sala de tela costuma ser assistida enquanto se trabalha em
 * outra coisa.
 *
 * So Chrome e Edge tem a API. Onde nao houver, o \`supported\` sai \`false\` e a
 * UI cai no PiP de video comum.
 */

interface DocumentPipApi {
    requestWindow: (options?: { width?: number; height?: number }) => Promise<Window>
    window: Window | null
}

function api(): DocumentPipApi | null {
    const withPip = window as Window & { documentPictureInPicture?: DocumentPipApi }
    return withPip.documentPictureInPicture ?? null
}

export function documentPipSupported(): boolean {
    return !!api()
}

/** PiP de um video so — o que existe em Firefox e Safari. */
export function videoPipSupported(): boolean {
    return typeof document !== 'undefined' && 'pictureInPictureEnabled' in document
        && (document as Document & { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled === true
}

export function useDocumentPip() {
    const [pipWindow, setPipWindow] = useState<Window | null>(null)
    const openingRef = useRef(false)

    const close = useCallback(() => {
        pipWindow?.close()
        setPipWindow(null)
    }, [pipWindow])

    /** Devolve se a janela abriu — quem chamou precisa desfazer o que preparou pra ela. */
    const open = useCallback(async (width = 640, height = 400): Promise<boolean> => {
        const pip = api()
        if (!pip || openingRef.current) return false
        openingRef.current = true

        try {
            const opened = await pip.requestWindow({ width, height })
            copyStyles(opened)
            // A janela some se a pessoa fechar no X: sem isso o React seguiria
            // renderizando num documento que nao existe mais.
            opened.addEventListener('pagehide', () => setPipWindow(null), { once: true })
            setPipWindow(opened)
            return true
        } catch {
            // Sem gesto do usuario ou janela recusada: segue na pagina mesmo.
            return false
        } finally {
            openingRef.current = false
        }
    }, [])

    // Fechar a aba nao pode deixar a janelinha orfa na tela.
    useEffect(() => () => {
        try {
            pipWindow?.close()
        } catch {
            // Ja fechada.
        }
    }, [pipWindow])

    return { pipWindow, open, close, supported: documentPipSupported() }
}

/**
 * Leva o CSS da pagina pra janela nova.
 *
 * A janela do PiP nasce com um documento em branco: sem isso o palco apareceria
 * sem estilo nenhum. Em producao o CSS vem de \`<link>\`, e em dev de \`<style>\`
 * injetado pelo Vite — os dois casos aparecem aqui.
 */
function copyStyles(target: Window) {
    for (const sheet of Array.from(document.styleSheets)) {
        try {
            const rules = Array.from(sheet.cssRules).map((rule) => rule.cssText).join('')
            const style = target.document.createElement('style')
            style.textContent = rules
            target.document.head.append(style)
        } catch {
            // Folha de outra origem nao deixa ler as regras: recria como link.
            if (sheet.href) {
                const link = target.document.createElement('link')
                link.rel = 'stylesheet'
                link.href = sheet.href
                target.document.head.append(link)
            }
        }
    }

    // O tema escuro vem de classe na raiz; sem copiar, a janela sai clara.
    target.document.documentElement.className = document.documentElement.className
    target.document.body.className = document.body.className
    target.document.body.style.margin = '0'
    target.document.body.style.background = 'black'
}
